import { Link, useParams } from "react-router-dom";
import JsonLd from "@/components/base/JsonLd";
import MangaCard from "@/components/feature/MangaCard";
import { mangaSeries } from "@/mocks/manga";

function badgeInfo(m: (typeof mangaSeries)[number]) {
  if (m.isOnHiatus) return { text: "断更", cls: "bg-background-300 text-foreground-700" };
  if (m.isBackfill) return { text: "补更", cls: "bg-accent-500 text-background-50" };
  if (m.isNew) return { text: "新更", cls: "bg-primary-500 text-background-50" };
  if (m.isToday) return { text: "今天更新", cls: "bg-primary-500 text-background-50" };
  return { text: "更新中", cls: "bg-secondary-500 text-background-50" };
}

export default function SeriesDetail() {
  const { id } = useParams();
  const manga = mangaSeries.find((m) => m.id === Number(id));

  if (!manga) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-24 text-center md:px-6">
        <i className="ri-book-2-line text-5xl text-foreground-300"></i>
        <h1 className="mt-4 font-display text-2xl text-foreground-900">未找到这部漫画</h1>
        <p className="mt-2 text-sm text-foreground-600">该作品可能已下架或地址有误。</p>
        <Link
          to="/series"
          className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm text-background-50 hover:bg-primary-600"
        >
          返回追更书架
        </Link>
      </div>
    );
  }

  const badge = badgeInfo(manga);
  const related = mangaSeries.filter((m) => m.id !== manga.id && m.category === manga.category).slice(0, 4);
  const fallbackRelated = mangaSeries.filter((m) => m.id !== manga.id).slice(0, 4);
  const relatedList = related.length > 0 ? related : fallbackRelated;

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://897kg.cc/" },
      { "@type": "ListItem", position: 2, name: "追更书架", item: "https://897kg.cc/series" },
      { "@type": "ListItem", position: 3, name: manga.title, item: `https://897kg.cc/series/${manga.id}` },
    ],
  };

  const bookSchema = {
    "@context": "https://schema.org",
    "@type": "Book",
    name: manga.title,
    author: { "@type": "Person", name: manga.author },
    genre: manga.category,
    inLanguage: "zh-CN",
    description: manga.intro,
    numberOfPages: manga.chapters.length,
    image: manga.cover,
    url: `https://897kg.cc/series/${manga.id}`,
  };

  return (
    <>
      <JsonLd id="jsonld-breadcrumb-detail" data={breadcrumbSchema} />
      <JsonLd id="jsonld-book" data={bookSchema} />

      <section className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-12">
        <nav aria-label="面包屑" className="mb-6 flex items-center gap-1.5 text-sm text-foreground-500">
          <Link to="/" className="hover:text-primary-600">首页</Link>
          <i className="ri-arrow-right-s-line text-foreground-400"></i>
          <Link to="/series" className="hover:text-primary-600">追更书架</Link>
          <i className="ri-arrow-right-s-line text-foreground-400"></i>
          <span className="text-foreground-700">{manga.title}</span>
        </nav>

        <div className="grid gap-6 md:grid-cols-3">
          <div className="md:col-span-1">
            <div className="overflow-hidden rounded-xl border border-background-200 bg-background-50">
              <img
                src={manga.cover}
                alt={`${manga.title} 封面`}
                title={`${manga.title} ${manga.lastChapter}`}
                className="aspect-[3/4] w-full object-cover object-top"
              />
            </div>
          </div>

          <div className="md:col-span-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`rounded-full px-3 py-1 text-xs font-medium ${badge.cls}`}>{badge.text}</span>
              <span className="rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-600">{manga.category}</span>
              <span className="rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-600">{manga.status}</span>
            </div>

            <h1 className="mt-4 font-display text-3xl text-foreground-900 md:text-4xl">{manga.title}</h1>
            <p className="mt-2 text-sm text-foreground-600">作者：{manga.author}</p>

            <p className="mt-5 max-w-2xl text-sm leading-relaxed text-foreground-700 md:text-base">
              {manga.intro}
            </p>

            <div className="mt-4 flex flex-wrap gap-2">
              {manga.tags.map((t) => (
                <span key={t} className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-800">
                  #{t}
                </span>
              ))}
            </div>

            <div className="mt-6 grid grid-cols-3 gap-3 sm:max-w-md">
              <div className="rounded-xl bg-background-100 p-3 text-center">
                <p className="text-xs text-foreground-500">读者</p>
                <p className="mt-1 font-display text-lg text-foreground-900">{manga.readers}</p>
              </div>
              <div className="rounded-xl bg-background-100 p-3 text-center">
                <p className="text-xs text-foreground-500">评分</p>
                <p className="mt-1 font-display text-lg text-accent-700">{manga.rating.toFixed(1)}</p>
              </div>
              <div className="rounded-xl bg-background-100 p-3 text-center">
                <p className="text-xs text-foreground-500">进度</p>
                <p className="mt-1 font-display text-lg text-primary-600">{manga.progress}%</p>
              </div>
            </div>

            <div className="mt-5">
              <div className="mb-2 flex items-center justify-between text-xs text-foreground-500">
                <span>追更进度</span>
                <span>{manga.progress}%</span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-background-200">
                <div className="h-full rounded-full bg-primary-500" style={{ width: `${manga.progress}%` }}></div>
              </div>
            </div>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <Link
                to={`/updates`}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-flashlight-line"></i>
                查看更新动态
              </Link>
              <Link
                to="/series"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border border-primary-300 px-6 py-3 text-sm font-medium text-primary-700 transition-colors hover:bg-primary-500 hover:text-background-50"
              >
                <i className="ri-book-2-line"></i>
                返回书架
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-6 md:px-6">
        <h2 className="mb-5 flex items-center gap-2 font-display text-xl text-foreground-900">
          <i className="ri-list-check-2 text-primary-500"></i>
          章节列表
        </h2>
        <div className="grid gap-3 sm:grid-cols-2">
          {manga.chapters.map((c, i) => (
            <div
              key={c.title}
              className={`flex items-center justify-between rounded-xl border px-4 py-3 ${
                i === 0
                  ? "border-primary-300 bg-primary-50"
                  : "border-background-200 bg-background-50"
              }`}
            >
              <span className="flex items-center gap-2 text-sm text-foreground-800">
                {i === 0 && <i className="ri-sparkling-line text-primary-500"></i>}
                {c.title}
              </span>
              <span className="shrink-0 text-xs text-foreground-500">{c.date}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
        <h2 className="mb-5 font-display text-xl text-foreground-900">相关推荐</h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
          {relatedList.map((m) => (
            <MangaCard key={m.id} manga={m} />
          ))}
        </div>
      </section>
    </>
  );
}